import React, { useState } from 'react';

interface ReportModalProps {
  isOpen: boolean;
  partnerId: string;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  onEndCall?: () => void;
}

const REPORT_REASONS = [
  'Inappropriate content',
  'Harassment',
  'Spam/advertising',
  'Suspected underage',
  'Other',
];

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  partnerId,
  onClose,
  onSubmit,
  onEndCall,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  if (!isOpen) {
    return null;
  }

  const handleSubmit = async () => {
    if (!selectedReason) {
      alert('Please select a reason for reporting');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(selectedReason);
      setIsSubmitted(true);

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose();
        setIsSubmitted(false);
        setSelectedReason('');
      }, 2000);
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEndCallAndReport = async () => {
    await handleSubmit();
    if (onEndCall) {
      onEndCall();
    }
  };

  if (isSubmitted) {
    return (
      <div style={styles.overlay} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.successTitle}>Report Submitted</h2>
            <p style={styles.successMessage}>
              Thank you for helping keep VibeTalk safe. We'll review this report promptly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>Report User</h2>
          <button onClick={onClose} style={styles.closeButton}>
            ✕
          </button>
        </div>

        {/* Warning */}
        <div style={styles.warningBox}>
          <span style={styles.warningIcon}>⚠️</span>
          <p style={styles.warningText}>
            False reports may result in action on your account. Please only report genuine safety concerns.
          </p>
        </div>

        {/* Reason Selection */}
        <div style={styles.content}>
          <p style={styles.label}>Why are you reporting this user?</p>
          <div style={styles.reasonsList}>
            {REPORT_REASONS.map((reason) => (
              <button
                key={reason}
                onClick={() => setSelectedReason(reason)}
                style={{
                  ...styles.reasonButton,
                  ...(selectedReason === reason ? styles.reasonButtonSelected : {}),
                }}
              >
                <div style={styles.radioCircle}>
                  {selectedReason === reason && <div style={styles.radioCircleInner} />}
                </div>
                <span style={styles.reasonText}>{reason}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          <button
            onClick={handleSubmit}
            disabled={!selectedReason || isSubmitting}
            style={{
              ...styles.submitButton,
              ...((!selectedReason || isSubmitting) ? styles.submitButtonDisabled : {}),
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
          <button
            onClick={handleEndCallAndReport}
            disabled={!selectedReason || isSubmitting}
            style={{
              ...styles.endCallButton,
              ...((!selectedReason || isSubmitting) ? styles.endCallButtonDisabled : {}),
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Report & End Call'}
          </button>
          <button onClick={onClose} style={styles.cancelButton}>
            Cancel
          </button>
        </div>

        {/* Privacy Notice */}
        <p style={styles.privacyNotice}>
          Your report is anonymous. The other user will not know who reported them.
        </p>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: 'var(--shadow-2xl)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: 'var(--color-dark-navy)',
    margin: 0,
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    color: 'var(--color-gray-500)',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1,
  },
  warningBox: {
    display: 'flex',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    backgroundColor: '#fff3cd',
    borderBottom: '1px solid #ffc107',
  },
  warningIcon: {
    fontSize: '1.25rem',
  },
  warningText: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#856404',
    lineHeight: '1.5',
  },
  content: {
    padding: '1.5rem',
  },
  label: {
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--color-dark-navy)',
    marginBottom: '1rem',
  },
  reasonsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  reasonButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: 'white',
    border: '2px solid var(--color-gray-200)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'left',
  },
  reasonButtonSelected: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  radioCircle: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '2px solid var(--color-gray-400)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioCircleInner: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
  },
  reasonText: {
    fontSize: '0.95rem',
    color: 'var(--color-dark-navy)',
    fontWeight: '500',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    padding: '0 1.5rem 1.5rem',
  },
  submitButton: {
    padding: '0.875rem 1.5rem',
    backgroundColor: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  submitButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  endCallButton: {
    padding: '0.875rem 1.5rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  endCallButtonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: '0.875rem 1.5rem',
    backgroundColor: 'white',
    color: 'var(--color-dark-navy)',
    border: '2px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  privacyNotice: {
    fontSize: '0.85rem',
    color: 'var(--color-gray-600)',
    textAlign: 'center',
    padding: '0 1.5rem 1.5rem',
    margin: 0,
    fontStyle: 'italic',
  },
  successContainer: {
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    fontSize: '3rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 1.5rem',
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: '1.75rem',
    fontWeight: '700',
    color: 'var(--color-dark-navy)',
    marginBottom: '0.75rem',
  },
  successMessage: {
    fontSize: '1rem',
    color: 'var(--color-gray-600)',
    lineHeight: '1.6',
    margin: 0,
  },
};
